import { useTheme } from '@mui/material'
import { useId } from 'react'

import { SPARKLES_CSS } from './animations/sparkles'

export type KennethAnimation = 'none' | 'sparkles'

interface KennethProps {
  size?: number
  color?: string
  animation?: KennethAnimation
}

const VIEWBOX = { x: 11, y: 5, width: 23, height: 34 }
const ASPECT_RATIO = VIEWBOX.width / VIEWBOX.height

const STAR_A = 'M19,11 L19.4,12.2 L20.6,12.6 L19.4,13 L19,14.2 L18.6,13 L17.4,12.6 L18.6,12.2 Z'
const STAR_B =
  'M22,13.2 L22.3,14.1 L23.2,14.4 L22.3,14.7 L22,15.6 L21.7,14.7 L20.8,14.4 L21.7,14.1 Z'
const STAR_C =
  'M17,14.8 L17.2,15.5 L17.9,15.7 L17.2,15.9 L17,16.6 L16.8,15.9 L16.1,15.7 L16.8,15.5 Z'

export const Kenneth = ({ size = 180, color, animation = 'none' }: KennethProps) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '_')
  const theme = useTheme()
  const kenneth = theme.palette.kenneth

  const lineColor = color ?? kenneth.lines
  const accentColor = kenneth.accent
  const bodyFill = kenneth.body

  const width = size * ASPECT_RATIO
  const height = size

  const isSparkles = animation === 'sparkles'

  return (
    <svg
      width={width}
      height={height}
      viewBox={`${VIEWBOX.x} ${VIEWBOX.y} ${VIEWBOX.width} ${VIEWBOX.height}`}
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-label='Kenneth the wizard cat'
      data-animation={animation}
    >
      {isSparkles && <style>{SPARKLES_CSS(uid)}</style>}

      {/* Hat */}
      <g id='kenneth-hat'>
        <path d='M20 8 L26 19 L14 19 Z' fill='#0071FF' stroke='#0071FF' strokeWidth='1' strokeLinejoin='round' />
        <line x1='14' y1='19' x2='26' y2='19' stroke='#0071FF' strokeWidth='1' />
      </g>

      {/* Hat stars — always in front of the hat */}
      <g id={`kenneth-hat-stars-${uid}`}>
        <path className='star-a' d={STAR_A} fill={accentColor} />
        <path className='star-b' d={STAR_B} fill={accentColor} />
        <path className='star-c' d={STAR_C} fill={accentColor} />
      </g>

      {/* Left Ear */}
      <g id='kenneth-left-ear'>
        <path d='M 14 20 L 12 16 L 16 19 Z' fill={bodyFill} stroke='none' />
        <path
          d='M 14 20 L 12 16 L 16 19'
          stroke={lineColor}
          strokeWidth='1'
          strokeLinecap='round'
          strokeLinejoin='round'
          fill='none'
        />
      </g>

      {/* Right Ear */}
      <g id='kenneth-right-ear'>
        <path d='M 26 20 L 28 16 L 24 19 Z' fill={bodyFill} stroke='none' />
        <path
          d='M 26 20 L 28 16 L 24 19'
          stroke={lineColor}
          strokeWidth='1'
          strokeLinecap='round'
          strokeLinejoin='round'
          fill='none'
        />
      </g>

      {/* Head — drawn after ears+hat so head circle covers brim overlap */}
      <g id='kenneth-head'>
        <circle cx='20' cy='24' r='6' fill={bodyFill} stroke={lineColor} strokeWidth='1' />
      </g>

      {/* Left Eye */}
      <g id='kenneth-left-eye'>
        <circle cx='17.5' cy='23.5' r='1.2' fill={lineColor} />
        <circle cx='17.8' cy='23.2' r='0.4' fill={bodyFill} />
        <circle cx='17.5' cy='23.5' r='2' stroke={lineColor} strokeWidth='0.6' fill='none' />
        <line x1='15.5' y1='23.5' x2='15.5' y2='20' stroke={lineColor} strokeWidth='0.6' strokeLinecap='round' />
      </g>

      {/* Right Eye */}
      <g id='kenneth-right-eye'>
        <circle cx='22.5' cy='23.5' r='1.2' fill={lineColor} />
        <circle cx='22.8' cy='23.2' r='0.4' fill={bodyFill} />
        <circle cx='22.5' cy='23.5' r='2' stroke={lineColor} strokeWidth='0.6' fill='none' />
        <line x1='19.5' y1='23.5' x2='20.5' y2='23.5' stroke={lineColor} strokeWidth='0.6' />
        <line x1='24.5' y1='23.5' x2='24.5' y2='20' stroke={lineColor} strokeWidth='0.6' strokeLinecap='round' />
      </g>

      {/* Nose */}
      <g id='kenneth-nose'>
        <circle cx='20' cy='26' r='0.6' fill={lineColor} />
      </g>

      {/* Mouth (placeholder for Phase 2) */}
      <g id='kenneth-mouth' />

      {/* Left Whiskers */}
      <g id='kenneth-left-whiskers'>
        <line x1='13' y1='25' x2='16' y2='25' stroke={lineColor} strokeWidth='0.6' strokeLinecap='round' />
        <line x1='13' y1='26.5' x2='16' y2='26' stroke={lineColor} strokeWidth='0.6' strokeLinecap='round' />
      </g>

      {/* Right Whiskers */}
      <g id='kenneth-right-whiskers'>
        <line x1='27' y1='25' x2='24' y2='25' stroke={lineColor} strokeWidth='0.6' strokeLinecap='round' />
        <line x1='27' y1='26.5' x2='24' y2='26' stroke={lineColor} strokeWidth='0.6' strokeLinecap='round' />
      </g>

      {/* Body */}
      <g id='kenneth-body'>
        <ellipse cx='20' cy='34' rx='5' ry='4' fill={bodyFill} stroke={lineColor} strokeWidth='1' />
      </g>

      {/* Left Foot */}
      <g id='kenneth-left-foot'>
        <circle cx='16' cy='37' r='1.5' stroke={lineColor} strokeWidth='0.8' fill={bodyFill} />
      </g>

      {/* Right Foot */}
      <g id='kenneth-right-foot'>
        <circle cx='24' cy='37' r='1.5' stroke={lineColor} strokeWidth='0.8' fill={bodyFill} />
      </g>

      {/* Wand */}
      <g id={`kenneth-wand-${uid}`}>
        <line x1='25' y1='33' x2='30' y2='30' stroke={lineColor} strokeWidth='0.8' strokeLinecap='round' />
        <path
          id={`kenneth-wand-star-1-${uid}`}
          d='M 30,27.8 L 30.6,29.4 L 32.2,30 L 30.6,30.6 L 30,32.2 L 29.4,30.6 L 27.8,30 L 29.4,29.4 Z'
          fill={accentColor}
        />
        <path
          id={`kenneth-wand-star-2-${uid}`}
          d='M 32,25.8 L 32.4,26.6 L 33.2,27 L 32.4,27.4 L 32,28.2 L 31.6,27.4 L 30.8,27 L 31.6,26.6 Z'
          fill={accentColor}
        />
        <path
          id={`kenneth-wand-star-3-${uid}`}
          d='M 33.5,25.2 L 33.8,25.7 L 34.3,26 L 33.8,26.3 L 33.5,26.8 L 33.2,26.3 L 32.7,26 L 33.2,25.7 Z'
          fill={accentColor}
        />
        <path
          id={`kenneth-wand-star-4-${uid}`}
          d='M 31,24.5 L 31.3,25.1 L 31.9,25.4 L 31.3,25.7 L 31,26.3 L 30.7,25.7 L 30.1,25.4 L 30.7,25.1 Z'
          fill={accentColor}
        />
      </g>
    </svg>
  )
}
